import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    AlertTriangle,
    Check,
    ChevronDown,
    ClipboardList,
    Copy,
    Loader2,
    MessageSquare,
    RefreshCw,
    Send,
    Share2,
    Sparkles,
    X,
} from "lucide-react";
import { Button } from "../ui/Button";
import { useToast } from "../../hooks/use-toast";
import GenerateNoteAction from "./GenerateNoteAction";
import GenerateTranscriptAction from "./GenerateTranscriptAction";
import UpdateTemplateAction from "./UpdateTemplateAction";
import AudioTranscriptReview from "./AudioTranscriptReview";
import SupportSection from "./SupportSection";
import { getPatientData, getAllDoctors } from "./patientApi";
import { isNoteLocked } from "./patientActionConfig";
import {
    summarizeTicket,
    analyzeSupportTicket,
    chatWithTicketAgent,
    escalateToSlack,
    shareAgentSession,
} from "./ticketApi";
import { sharedSessionUrl } from "./supportUrls";
import { collectValidatorResults } from "./ticketValidators";
import MarkdownText from "./MarkdownText";
import { chatSurfaceClasses, chatSurfaceStyle } from "./chatBackground";
import {
    supportContextCardTitle,
    supportInsetPanelAuto,
    supportSubpanelHeader,
} from "./supportStyles";

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const ACTION_LABELS = {
    get_more_context: "Get more context",
    generate_transcript: "Generate transcript",
    generate_note: "Generate note",
    update_template: "Update template",
    review_audio: "Review audio",
    escalate_to_engineering: "Escalate to engineering",
};

const ACTION_BADGE_CLASSES = {
    get_more_context: "bg-blue-50 text-blue-700 border-blue-300",
    generate_transcript: "bg-violet-50 text-violet-700 border-violet-300",
    generate_note: "bg-violet-50 text-violet-700 border-violet-300",
    update_template: "bg-indigo-50 text-indigo-700 border-indigo-300",
    review_audio: "bg-amber-50 text-amber-700 border-amber-300",
    escalate_to_engineering: "bg-red-50 text-red-700 border-red-300",
};

const ISSUE_CATEGORY_LABELS = {
    missing_transcript: "Missing transcript",
    missing_note: "Missing note",
    wrong_note: "Wrong note",
    recording_issue: "Recording issue",
    account_issue: "Account issue",
    other: "Other",
};

const STEP_TOOL_LABELS = {
    lookup_patient: "Looked up patient",
    lookup_doctor: "Looked up doctor",
    get_lambda_insights: "Fetched pipeline runs",
    get_ticket: "Fetched ticket",
    get_patient_artifacts: "Fetched patient artifacts",
    get_patient_templates: "Fetched patient templates",
    get_doctor_templates: "Fetched doctor templates",
    get_patient_smart_chart: "Fetched Smart Chart",
    get_playbook: "Consulted support playbook",
};

/**
 * Plain-text technical summary from the escalation payload's structured
 * fields, falling back to the legacy single-string form.
 */
const composeEngineeringSummary = (payload = {}) => {
    const lines = [
        payload.reported && `Reported: ${payload.reported}`,
        payload.identifiers && `Identifiers: ${payload.identifiers}`,
        payload.pipeline_findings && `Pipeline: ${payload.pipeline_findings}`,
        payload.already_tried && `Tried: ${payload.already_tried}`,
        Array.isArray(payload.investigate) &&
            payload.investigate.length > 0 &&
            `Investigate:\n${payload.investigate
                .map((q, i) => `${i + 1}. ${q}`)
                .join("\n")}`,
    ].filter(Boolean);
    return lines.length > 0
        ? lines.join("\n")
        : payload.engineering_summary || "";
};

const describeStepInput = (input = {}) => {
    const parts = Object.entries(input)
        .filter(
            ([, value]) =>
                value !== null && value !== undefined && value !== "",
        )
        .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`);
    return parts.join(", ");
};

// ─────────────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────────────

const AgentSteps = ({ steps }) => {
    const [expanded, setExpanded] = useState(false);
    if (!Array.isArray(steps) || steps.length === 0) return null;

    return (
        <div className={`${supportInsetPanelAuto} overflow-hidden`}>
            <button
                type="button"
                onClick={() => setExpanded((open) => !open)}
                aria-expanded={expanded}
                className={`w-full flex items-center justify-between gap-2 text-left hover:bg-slate-100 transition-colors ${
                    expanded ? supportSubpanelHeader : "px-3 py-2 bg-slate-50"
                }`}
            >
                <h4 className={supportContextCardTitle}>
                    Agent investigation{" "}
                    <span className="font-normal text-gray-500">
                        ({steps.length} step{steps.length === 1 ? "" : "s"})
                    </span>
                </h4>
                <ChevronDown
                    className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
                        expanded ? "rotate-180" : ""
                    }`}
                    aria-hidden
                />
            </button>
            {expanded && (
                <ol className="px-3 py-2.5 space-y-1.5">
                    {steps.map((step, index) => (
                        <li
                            key={index}
                            className="flex items-baseline gap-2 text-sm"
                        >
                            <span className="text-[11px] text-gray-400 shrink-0 w-4 text-right">
                                {index + 1}.
                            </span>
                            <span className="text-gray-800 min-w-0">
                                <span className="font-medium">
                                    {STEP_TOOL_LABELS[step.tool] || step.tool}
                                </span>
                                {describeStepInput(step.input) && (
                                    <span className="text-gray-500">
                                        {" "}
                                        ({describeStepInput(step.input)})
                                    </span>
                                )}
                                {step.summary && (
                                    <span className="text-gray-500">
                                        {" "}
                                        — {step.summary}
                                    </span>
                                )}
                            </span>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
};

const CopyableText = ({ label, text }) => {
    const { toast } = useToast();

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            toast({ title: "Copied to clipboard" });
        } catch {
            toast({
                variant: "destructive",
                title: "Could not copy to clipboard",
            });
        }
    };

    return (
        <div className={`${supportInsetPanelAuto} overflow-hidden`}>
            <div
                className={`${supportSubpanelHeader} flex items-center justify-between gap-2`}
            >
                <h4 className={supportContextCardTitle}>{label}</h4>
                <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1 text-xs font-medium text-ktx-purple hover:underline"
                >
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                    Copy
                </button>
            </div>
            <p className="px-3 py-2.5 text-sm text-gray-800 whitespace-pre-wrap break-words">
                {text}
            </p>
        </div>
    );
};

/**
 * Sends the escalation to the engineering Slack channel: the agent's
 * reasoning, technical summary, and pipeline errors (gathered server-side),
 * plus an optional custom message from the CS agent.
 */
const SlackEscalation = ({
    ticketId,
    patientId,
    payload,
    sessionId,
    buildShareUi,
}) => {
    const [customMessage, setCustomMessage] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [sent, setSent] = useState(null);
    const [sendError, setSendError] = useState(null);
    // Pair the task ID with the ticket it was created for so it can never
    // leak across tickets, even in the render before an effect runs.
    const [retryClickup, setRetryClickup] = useState(null);

    const handleSend = async () => {
        setIsSending(true);
        setSendError(null);

        let shareUrl;
        if (sessionId) {
            const ui = buildShareUi?.() || { ticket_id: ticketId };
            const shareData = await shareAgentSession({ sessionId, ui });
            if (!shareData.error && shareData.share_id) {
                shareUrl = sharedSessionUrl({
                    shareId: shareData.share_id,
                    ticketId: ui.ticket_id || ticketId || null,
                    patientId: ui.patient_id || patientId || null,
                });
            }
        }

        const clickupTaskId =
            retryClickup?.ticketId === ticketId
                ? retryClickup.taskId
                : undefined;

        const data = await escalateToSlack({
            ticketId,
            patientId,
            reason: payload.reason,
            customMessage: customMessage.trim() || undefined,
            shareUrl,
            payload,
            clickupTaskId,
        });

        setIsSending(false);
        if (data.error) {
            if (data.clickup_task_id) {
                setRetryClickup({ ticketId, taskId: data.clickup_task_id });
            }
            setSendError(data.error);
            return;
        }
        setSent({
            channel: data.channel || "Slack",
            slackPermalink: data.slack_permalink || null,
            clickupUrl: data.clickup_url || null,
            clickupError: data.clickup_error || null,
        });
    };

    if (sent) {
        return (
            <div className="space-y-2">
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 flex items-center gap-1.5">
                    <Check className="h-4 w-4 shrink-0" aria-hidden />
                    <span>
                        Escalation posted to the Pylon ticket and mirrored
                        into <span className="font-semibold">Slack</span> —
                        replies sync both ways.
                    </span>
                </p>
                {(sent.slackPermalink || sent.clickupUrl) && (
                    <div className="flex flex-wrap gap-3 px-3 text-sm">
                        {sent.slackPermalink && (
                            <a
                                href={sent.slackPermalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 font-medium text-ktx-purple hover:underline"
                            >
                                <MessageSquare
                                    className="h-3.5 w-3.5 shrink-0"
                                    aria-hidden
                                />
                                Slack thread
                            </a>
                        )}
                        {sent.clickupUrl && (
                            <a
                                href={sent.clickupUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 font-medium text-ktx-purple hover:underline"
                            >
                                <ClipboardList
                                    className="h-3.5 w-3.5 shrink-0"
                                    aria-hidden
                                />
                                ClickUp task
                            </a>
                        )}
                    </div>
                )}
                {sent.clickupError && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-1.5">
                        <AlertTriangle
                            className="h-4 w-4 shrink-0 mt-0.5"
                            aria-hidden
                        />
                        <span>
                            The escalation went out, but the ClickUp task could
                            not be created — please open one manually.
                        </span>
                    </p>
                )}
            </div>
        );
    }

    return (
        <div className={`${supportInsetPanelAuto} overflow-hidden`}>
            <div className={supportSubpanelHeader}>
                <h4 className={supportContextCardTitle}>
                    Notify engineering on Slack
                </h4>
            </div>
            <div className="px-3 py-2.5 space-y-2">
                <p className="text-xs text-gray-500">
                    Opens an internal thread on the Pylon ticket that mirrors
                    into the engineering Slack channel, so replies sync both
                    ways, and opens a linked ClickUp task. Add a note if you have
                    extra context.
                </p>
                <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={2}
                    placeholder="Optional message from you, e.g. what you already tried or how urgent this is…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple resize-y"
                />
                {sendError && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                        {sendError}
                    </p>
                )}
                <Button
                    type="button"
                    onClick={handleSend}
                    disabled={isSending}
                    className="bg-ktx-purple hover:bg-purple-700 text-white"
                >
                    {isSending ? (
                        <Loader2
                            className="h-4 w-4 mr-1.5 animate-spin"
                            aria-hidden
                        />
                    ) : (
                        <Send className="h-4 w-4 mr-1.5" aria-hidden />
                    )}
                    {isSending ? "Sending…" : "Send to Slack"}
                </Button>
            </div>
        </div>
    );
};

const LiveActivity = ({ items }) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    return (
        <div className={`${supportInsetPanelAuto} overflow-hidden`}>
            <div className={supportSubpanelHeader}>
                <h4 className={supportContextCardTitle}>Analyzing…</h4>
            </div>
            <ol className="px-3 py-2.5 space-y-1.5">
                {items.map((item, index) => {
                    const isCurrent = index === items.length - 1;
                    return (
                        <li
                            key={index}
                            className="flex items-baseline gap-2 text-sm"
                        >
                            <span className="shrink-0 w-4 flex justify-center translate-y-0.5">
                                {isCurrent ? (
                                    <Loader2
                                        className="h-3.5 w-3.5 text-ktx-purple animate-spin"
                                        aria-hidden
                                    />
                                ) : (
                                    <Check
                                        className="h-3.5 w-3.5 text-green-600"
                                        aria-hidden
                                    />
                                )}
                            </span>
                            <span
                                className={`min-w-0 ${isCurrent ? "text-gray-900" : "text-gray-500"}`}
                            >
                                {item.label}
                                {item.detail && (
                                    <span className="text-gray-400">
                                        {" "}
                                        ({item.detail})
                                    </span>
                                )}
                                {item.summary && (
                                    <span className="text-gray-400">
                                        {" "}
                                        — {item.summary}
                                    </span>
                                )}
                            </span>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
};

const PatientActionRunner = ({ patientId, action }) => {
    const [patient, setPatient] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!patientId) {
            setPatient(null);
            setLoadError(null);
            return undefined;
        }

        setIsLoading(true);
        setLoadError(null);
        getPatientData(patientId).then((data) => {
            if (cancelled) return;
            setIsLoading(false);
            if (data && !data.error && Object.keys(data).length > 0) {
                setPatient(data);
            } else {
                setPatient(null);
                setLoadError(
                    `Patient ${patientId} not found. Check the patient ID.`,
                );
            }
        });

        return () => {
            cancelled = true;
        };
    }, [patientId]);

    if (!patientId) {
        return (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                No patient ID available. Provide the patient details and re-run
                the analysis, or ask the requester for the patient name.
            </p>
        );
    }

    if (isLoading) {
        return (
            <p className="text-sm text-gray-500">
                Loading patient {patientId}…
            </p>
        );
    }

    if (loadError) {
        return (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {loadError}
            </p>
        );
    }

    if (!patient) return null;

    return (
        <div className="space-y-2">
            <p className="text-sm text-gray-700">
                Patient:{" "}
                <span className="font-medium">
                    {patient.description || "—"}
                </span>
                <span className="text-gray-500">
                    {" "}
                    · ID {patient.patient_id}
                </span>
            </p>
            {action === "generate_transcript" ? (
                <GenerateTranscriptAction patient={patient} />
            ) : isNoteLocked(patient.status) ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    This patient&apos;s note is Locked, so it cannot be
                    regenerated. Escalate to engineering if the note still needs
                    a change.
                </p>
            ) : (
                <GenerateNoteAction patientId={patient.patient_id} />
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────
// Recommendation card
// ─────────────────────────────────────────────────────────────────

const RecommendationCard = ({ result, ticketId, sessionId, buildShareUi }) => {
    const { action, payload = {} } = result;
    const effectivePatientId =
        result.resolved_patient?.patient_id || payload.patient_id || null;
    const effectiveTicketId = ticketId || payload.ticket_number || null;

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
                <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                        ACTION_BADGE_CLASSES[action] ||
                        "bg-gray-100 text-gray-700 border-gray-300"
                    }`}
                >
                    {ACTION_LABELS[action] || action}
                </span>
                {result.resolved_patient && (
                    <span className="text-xs text-gray-600">
                        Patient identified:{" "}
                        <span className="font-medium">
                            {result.resolved_patient.description || "—"}
                        </span>{" "}
                        (ID {result.resolved_patient.patient_id})
                    </span>
                )}
                {result.model && (
                    <span className="text-[11px] text-gray-400">
                        via {result.model}
                    </span>
                )}
            </div>

            <AgentSteps steps={result.steps} />

            {payload.reason && (
                <p className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">Why: </span>
                    {payload.reason}
                </p>
            )}

            {action === "get_more_context" && (
                <>
                    {Array.isArray(payload.missing_info) &&
                        payload.missing_info.length > 0 && (
                            <div>
                                <p className="text-xs font-medium text-gray-600 mb-1">
                                    Missing information
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-800 space-y-0.5">
                                    {payload.missing_info.map((item, index) => (
                                        <li key={index}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    {payload.suggested_reply && (
                        <CopyableText
                            label="Suggested reply"
                            text={payload.suggested_reply}
                        />
                    )}
                </>
            )}

            {(action === "generate_transcript" ||
                action === "generate_note") && (
                <PatientActionRunner
                    patientId={effectivePatientId}
                    action={action}
                />
            )}

            {action === "update_template" && (
                <UpdateTemplateAction
                    payload={payload}
                    patientId={effectivePatientId}
                />
            )}

            {action === "review_audio" && (
                <>
                    {Array.isArray(payload.what_to_check) &&
                        payload.what_to_check.length > 0 && (
                            <div>
                                <p className="text-xs font-medium text-gray-600 mb-1">
                                    What to check
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-800 space-y-0.5">
                                    {payload.what_to_check.map((item, index) => (
                                        <li key={index}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    <AudioTranscriptReview patientId={effectivePatientId} />
                    {payload.suggested_reply && (
                        <CopyableText
                            label="Reply if the audio has no usable speech"
                            text={payload.suggested_reply}
                        />
                    )}
                </>
            )}

            {action === "escalate_to_engineering" && (
                <>
                    {composeEngineeringSummary(payload) && (
                        <CopyableText
                            label="Summary for engineering"
                            text={composeEngineeringSummary(payload)}
                        />
                    )}
                    <SlackEscalation
                        ticketId={effectiveTicketId}
                        patientId={effectivePatientId}
                        payload={payload}
                        sessionId={sessionId}
                        buildShareUi={buildShareUi}
                    />
                </>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────
// Chat bubbles
// ─────────────────────────────────────────────────────────────────

const UserChatBubble = ({ text }) => (
    <div className="flex justify-end">
        <p className="max-w-[85%] rounded-2xl rounded-br-md bg-ktx-purple text-white text-[16px] leading-relaxed px-4 py-2.5 shadow-sm whitespace-pre-wrap break-words">
            {text}
        </p>
    </div>
);

const AgentChatBubble = ({ text }) => (
    <div className="flex">
        <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-white border border-gray-200 px-4 py-2.5 shadow-sm">
            <MarkdownText text={text} />
        </div>
    </div>
);

/**
 * Multi-line chat composer that grows with its content (up to ~6 lines).
 * Enter sends, Shift+Enter inserts a newline.
 */
const ChatComposer = ({ value, onChange, onSubmit, disabled, placeholder }) => {
    const textareaRef = useRef(null);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [value]);

    const submit = () => {
        if (disabled || !value.trim()) return;
        onSubmit();
    };

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                submit();
            }}
            className="flex items-end gap-2"
        >
            <textarea
                ref={textareaRef}
                rows={1}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        submit();
                    }
                }}
                disabled={disabled}
                placeholder={placeholder}
                className="flex-1 resize-none overflow-y-auto px-3.5 py-2.5 border border-gray-300 rounded-xl shadow-sm text-[16px] leading-relaxed bg-white focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple disabled:bg-gray-50 disabled:text-gray-400"
            />
            <Button
                type="submit"
                disabled={disabled || !value.trim()}
                className="bg-ktx-purple hover:bg-purple-700 text-white shrink-0"
            >
                <Send className="h-4 w-4 mr-1.5" aria-hidden />
                Send
            </Button>
        </form>
    );
};

// ─────────────────────────────────────────────────────────────────
// Multi-select tag inputs
// ─────────────────────────────────────────────────────────────────

const Tag = ({ label, invalid, onRemove }) => (
    <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
            invalid
                ? "bg-red-50 text-red-700 border-red-300"
                : "bg-purple-50 text-ktx-purple border-purple-200"
        }`}
    >
        {label}
        <button
            type="button"
            onClick={onRemove}
            className="hover:opacity-70"
            aria-label={`Remove ${label}`}
        >
            <X className="h-3 w-3" aria-hidden />
        </button>
    </span>
);

/**
 * Multi-select patient IDs. Each added ID is validated in the background;
 * the tag shows the patient name once loaded, or turns red if not found.
 * Tags: [{ id, label, invalid }]
 */
const PatientTagSelect = ({ tags, onChange }) => {
    const [input, setInput] = useState("");
    const [inputError, setInputError] = useState(null);

    const addPatient = () => {
        const trimmed = input.trim();
        if (!trimmed) return;
        if (!/^\d+$/.test(trimmed)) {
            setInputError("Patient ID must be a number.");
            return;
        }
        if (tags.some((t) => t.id === trimmed)) {
            setInput("");
            return;
        }
        setInputError(null);
        setInput("");
        onChange([
            ...tags,
            { id: trimmed, label: `#${trimmed}`, loading: true },
        ]);

        getPatientData(trimmed).then((data) => {
            const found = data && !data.error && Object.keys(data).length > 0;
            onChange((prev) =>
                prev.map((t) =>
                    t.id === trimmed
                        ? {
                              id: trimmed,
                              label: found
                                  ? `${data.description || "Unnamed"} (#${trimmed})`
                                  : `#${trimmed} (not found)`,
                              invalid: !found,
                          }
                        : t,
                ),
            );
        });
    };

    return (
        <div>
            <div className="flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 min-h-[2.5rem] border border-gray-300 rounded-md shadow-sm bg-white focus-within:ring-2 focus-within:ring-ktx-purple focus-within:border-ktx-purple">
                {tags.map((tag) => (
                    <Tag
                        key={tag.id}
                        label={tag.label}
                        invalid={tag.invalid}
                        onRemove={() =>
                            onChange(tags.filter((t) => t.id !== tag.id))
                        }
                    />
                ))}
                <input
                    type="text"
                    value={input}
                    onChange={(e) => {
                        setInput(e.target.value);
                        setInputError(null);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            addPatient();
                        }
                        if (
                            e.key === "Backspace" &&
                            !input &&
                            tags.length > 0
                        ) {
                            onChange(tags.slice(0, -1));
                        }
                    }}
                    onBlur={addPatient}
                    placeholder={
                        tags.length === 0
                            ? "Type a patient ID and press Enter…"
                            : "Add another…"
                    }
                    className="flex-1 min-w-[9rem] border-0 p-0.5 text-sm focus:outline-none focus:ring-0"
                />
            </div>
            {inputError && (
                <p className="mt-0.5 text-[11px] text-red-600">{inputError}</p>
            )}
        </div>
    );
};

/**
 * Multi-select doctors via search dropdown. Tags are full doctor objects.
 */
const DoctorTagSelect = ({ doctors, onChange }) => {
    const [allDoctors, setAllDoctors] = useState([]);
    const [search, setSearch] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        getAllDoctors().then((docs) =>
            setAllDoctors(Array.isArray(docs) ? docs : []),
        );
    }, []);

    const selectedIds = new Set(doctors.map((d) => String(d.user_id)));
    const filtered =
        search.trim().length > 0
            ? allDoctors.filter((doc) => {
                  if (selectedIds.has(String(doc.user_id))) return false;
                  const s = search.trim().toLowerCase();
                  return (
                      (doc.user_name || "").toLowerCase().includes(s) ||
                      (doc.user_email || "").toLowerCase().includes(s)
                  );
              })
            : [];

    return (
        <div className="relative">
            <div className="flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 min-h-[2.5rem] border border-gray-300 rounded-md shadow-sm bg-white focus-within:ring-2 focus-within:ring-ktx-purple focus-within:border-ktx-purple">
                {doctors.map((doc) => (
                    <Tag
                        key={doc.user_id}
                        label={doc.user_name || doc.user_email || doc.user_id}
                        onRemove={() =>
                            onChange(
                                doctors.filter(
                                    (d) => d.user_id !== doc.user_id,
                                ),
                            )
                        }
                    />
                ))}
                <input
                    type="text"
                    autoComplete="off"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    onKeyDown={(e) => {
                        if (
                            e.key === "Backspace" &&
                            !search &&
                            doctors.length > 0
                        ) {
                            onChange(doctors.slice(0, -1));
                        }
                    }}
                    placeholder={
                        doctors.length === 0
                            ? "Search by name or email to add a doctor…"
                            : "Add another…"
                    }
                    className="flex-1 min-w-[9rem] border-0 p-0.5 text-sm focus:outline-none focus:ring-0"
                />
            </div>
            {showDropdown && search.trim() && filtered.length > 0 && (
                <div className="absolute z-10 w-full mt-1 max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg">
                    {filtered.slice(0, 15).map((doc) => (
                        <button
                            key={doc.user_id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm border-b border-gray-100 last:border-b-0"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                onChange([...doctors, doc]);
                                setSearch("");
                                setShowDropdown(false);
                            }}
                        >
                            <span className="font-medium text-gray-900">
                                {doc.user_name}
                            </span>
                            {doc.user_email && (
                                <span className="text-xs text-gray-500 ml-1">
                                    ({doc.user_email})
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────
// Phase 1: Summary + Context form
// ─────────────────────────────────────────────────────────────────

const TicketSummaryForm = ({
    ticketId,
    doctor,
    onStartAnalysis,
    onSelectionChange,
}) => {
    const [summaryData, setSummaryData] = useState(null);
    const [isSummarizing, setIsSummarizing] = useState(false);
    const [summaryError, setSummaryError] = useState(null);

    // CS-provided context fields
    const [patientTags, setPatientTags] = useState([]);
    const [doctorTags, setDoctorTags] = useState(doctor ? [doctor] : []);
    const [additionalNotes, setAdditionalNotes] = useState("");
    const [isPreparing, setIsPreparing] = useState(false);

    useEffect(() => {
        if (!ticketId) return;
        let cancelled = false;
        setIsSummarizing(true);
        setSummaryError(null);

        summarizeTicket(ticketId).then((data) => {
            if (cancelled) return;
            setIsSummarizing(false);
            if (data.error) {
                setSummaryError(data.error);
                return;
            }
            setSummaryData(data);
        });

        return () => {
            cancelled = true;
        };
    }, [ticketId]);

    // Pre-select the requester-matched doctor once it arrives.
    useEffect(() => {
        if (!doctor?.user_id) return;
        setDoctorTags((prev) =>
            prev.some((d) => String(d.user_id) === String(doctor.user_id))
                ? prev
                : [doctor, ...prev],
        );
    }, [doctor]);

    // Keep the page's Doctor & Patient Info section in sync with selections.
    useEffect(() => {
        onSelectionChange?.({
            patientIds: patientTags.filter((t) => !t.invalid).map((t) => t.id),
            doctors: doctorTags,
        });
    }, [patientTags, doctorTags, onSelectionChange]);

    const handleRunAnalysis = async () => {
        const patientIds = patientTags
            .filter((t) => !t.invalid)
            .map((t) => t.id);

        // Run the CS validators for each selected patient so the agent gets the
        // same health-check evidence a CS agent sees on the patient page.
        setIsPreparing(true);
        let validatorResults = [];
        if (patientIds.length > 0) {
            const collected = await Promise.all(
                patientIds.map((pid) =>
                    collectValidatorResults(pid).catch(() => null),
                ),
            );
            validatorResults = collected.filter(Boolean);
        }
        setIsPreparing(false);

        onStartAnalysis({
            patient_ids: patientIds,
            doctor_ids: doctorTags.map((d) => String(d.user_id)),
            additional_notes: additionalNotes.trim() || undefined,
            validator_results: validatorResults,
        });
    };

    return (
        <div className="space-y-4">
            {/* Summary */}
            {isSummarizing && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader2
                        className="h-4 w-4 animate-spin text-ktx-purple"
                        aria-hidden
                    />
                    Reading the ticket…
                </div>
            )}

            {summaryError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {summaryError}
                </p>
            )}

            {summaryData && (
                <div className={`${supportInsetPanelAuto} overflow-hidden`}>
                    <div
                        className={`${supportSubpanelHeader} flex items-center justify-between gap-2`}
                    >
                        <h4 className={supportContextCardTitle}>
                            <Sparkles
                                className="inline h-3.5 w-3.5 mr-1 text-ktx-purple -translate-y-px"
                                aria-hidden
                            />
                            Ticket summary
                        </h4>
                        {summaryData.issue_category && (
                            <span className="text-[11px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                                {ISSUE_CATEGORY_LABELS[
                                    summaryData.issue_category
                                ] || summaryData.issue_category}
                            </span>
                        )}
                    </div>
                    <div className="px-3 py-2.5 space-y-2">
                        <p className="text-sm text-gray-800">
                            {summaryData.summary}
                        </p>
                        {(summaryData.patient_name ||
                            summaryData.appointment_date) && (
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                                {summaryData.patient_name && (
                                    <span>
                                        Patient mentioned:{" "}
                                        <span className="font-medium text-gray-700">
                                            {summaryData.patient_name}
                                        </span>
                                    </span>
                                )}
                                {summaryData.appointment_date && (
                                    <span>
                                        Appointment:{" "}
                                        <span className="font-medium text-gray-700">
                                            {summaryData.appointment_date}
                                        </span>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Context fields */}
            <div>
                <p className="text-sm font-medium text-gray-700">
                    Add or edit context before analysis
                </p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Patients
                        </label>
                        <PatientTagSelect
                            tags={patientTags}
                            onChange={setPatientTags}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Doctors
                        </label>
                        <DoctorTagSelect
                            doctors={doctorTags}
                            onChange={setDoctorTags}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Additional notes for the agent
                        </label>
                        <textarea
                            value={additionalNotes}
                            onChange={(e) => setAdditionalNotes(e.target.value)}
                            rows={2}
                            placeholder="Any extra context, corrections, or instructions…"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple resize-y"
                        />
                    </div>
                </div>
            </div>

            {/* Run analysis button */}
            <div className="flex items-center gap-3">
                <Button
                    type="button"
                    onClick={handleRunAnalysis}
                    disabled={isSummarizing || isPreparing}
                    className="bg-ktx-purple hover:bg-purple-700 text-white"
                >
                    {isPreparing ? (
                        <Loader2
                            className="h-4 w-4 mr-1.5 animate-spin"
                            aria-hidden
                        />
                    ) : (
                        <Sparkles className="h-4 w-4 mr-1.5" aria-hidden />
                    )}
                    {isPreparing ? "Running health checks…" : "Run analysis"}
                </Button>
                {!summaryData && !isSummarizing && !isPreparing && (
                    <span className="text-xs text-gray-400">
                        You can run the analysis without waiting for the summary
                    </span>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────
// Share button (shared with SupportAgentChat)
// ─────────────────────────────────────────────────────────────────

const ShareSessionButton = ({ sessionId, buildUi }) => {
    const { toast } = useToast();
    const [isSharing, setIsSharing] = useState(false);

    const handleShare = async (e) => {
        e?.stopPropagation();
        if (!sessionId || isSharing) return;
        setIsSharing(true);
        const ui = buildUi();
        const data = await shareAgentSession({ sessionId, ui });
        setIsSharing(false);

        if (data.error) {
            toast({
                variant: "destructive",
                title: "Could not share this session",
                description: data.error,
            });
            return;
        }

        const url = sharedSessionUrl({
            shareId: data.share_id,
            ticketId: ui.ticket_id || null,
            patientId: ui.patient_id || null,
        });
        let copied = false;
        try {
            await navigator.clipboard.writeText(url);
            copied = true;
        } catch {
            // Clipboard can be unavailable (e.g. non-secure context).
        }
        toast({
            title: copied ? "Share link copied" : "Share link created",
            description: copied
                ? "Teammates who open it get the full conversation and continue on their own copy."
                : url,
        });
    };

    return (
        <button
            type="button"
            onClick={handleShare}
            disabled={!sessionId || isSharing}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-ktx-purple disabled:opacity-50 disabled:hover:text-gray-500"
        >
            {isSharing ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : (
                <Share2 className="h-3 w-3" aria-hidden />
            )}
            {isSharing ? "Sharing…" : "Share session"}
        </button>
    );
};

// ─────────────────────────────────────────────────────────────────
// Phase 2: Conversation
// ─────────────────────────────────────────────────────────────────

const TicketConversation = ({
    ticketId,
    csContext,
    onRestart,
    onSessionReady,
    onPatientResolved,
    restored = null,
}) => {
    const { toast } = useToast();
    const [conversation, setConversation] = useState(
        restored?.conversation || [],
    );
    const [sessionId, setSessionId] = useState(restored?.sessionId || null);
    const [chatInput, setChatInput] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [activity, setActivity] = useState([]);
    const [agentError, setAgentError] = useState(null);
    const conversationEndRef = useRef(null);
    // Exposed to the parent so the header Share button can snapshot the
    // current rendered conversation without lifting all state.
    const conversationRef = useRef(conversation);
    conversationRef.current = conversation;

    const scrollToBottom = useCallback(() => {
        conversationEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [conversation, activity, scrollToBottom]);

    // Bubble up restored session ID immediately so the header Share button
    // is available without waiting for a new analysis.
    useEffect(() => {
        if (restored?.sessionId)
            onSessionReady?.(restored.sessionId, conversationRef);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAgentEvent = useCallback(
        (event) => {
            if (
                event.type === "patient_resolved" &&
                event.patient?.patient_id
            ) {
                onPatientResolved?.(event.patient.patient_id);
            }
            setActivity((prev) => {
                if (event.type === "status") {
                    return [...prev, { label: event.message }];
                }
                if (event.type === "tool_start") {
                    return [
                        ...prev,
                        {
                            tool: event.tool,
                            label: `${STEP_TOOL_LABELS[event.tool] || event.tool}`,
                            detail: describeStepInput(event.input),
                        },
                    ];
                }
                if (event.type === "step") {
                    let index = -1;
                    for (let i = prev.length - 1; i >= 0; i--) {
                        if (prev[i].tool === event.tool && !prev[i].summary) {
                            index = i;
                            break;
                        }
                    }
                    if (index === -1) return prev;
                    const next = [...prev];
                    next[index] = { ...next[index], summary: event.summary };
                    return next;
                }
                if (event.type === "patient_resolved") {
                    const patient = event.patient || {};
                    return [
                        ...prev,
                        {
                            label: `Patient identified: ${patient.description || "—"} (ID ${patient.patient_id})`,
                        },
                    ];
                }
                return prev;
            });
        },
        [onPatientResolved],
    );

    // Kick off analysis on mount (skipped when restored from a share — the
    // forked session already carries the analysis and conversation).
    useEffect(() => {
        if (restored) return undefined;
        const controller = new AbortController();
        setIsProcessing(true);
        setAgentError(null);
        setActivity([]);

        analyzeSupportTicket(ticketId, {
            csContext,
            signal: controller.signal,
            onEvent: handleAgentEvent,
        }).then((data) => {
            if (data.aborted) return;
            setIsProcessing(false);
            if (data.error) {
                setAgentError(data.error);
                toast({
                    variant: "destructive",
                    title: "Analysis failed",
                    description: data.error,
                });
                return;
            }
            if (data.resolved_patient?.patient_id) {
                onPatientResolved?.(data.resolved_patient.patient_id);
            }
            const sid = data.session_id || null;
            setSessionId(sid);
            onSessionReady?.(sid, conversationRef);
            setConversation([
                data.type === "reply"
                    ? { kind: "reply", text: data.text }
                    : { kind: "recommendation", result: data },
            ]);
        });

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId]);

    const handleSendMessage = async () => {
        const message = chatInput.trim();
        if (!message || isProcessing || !sessionId) return;

        setChatInput("");
        setAgentError(null);
        setActivity([]);
        setConversation((prev) => [...prev, { kind: "user", text: message }]);
        setIsProcessing(true);

        const data = await chatWithTicketAgent(ticketId, {
            sessionId,
            message,
            onEvent: handleAgentEvent,
        });

        setIsProcessing(false);

        if (data.aborted) return;

        if (data.error) {
            setAgentError(data.error);
            return;
        }

        if (data.resolved_patient?.patient_id) {
            onPatientResolved?.(data.resolved_patient.patient_id);
        }

        if (data.type === "reply") {
            setConversation((prev) => [
                ...prev,
                { kind: "reply", text: data.text },
            ]);
        } else {
            setConversation((prev) => [
                ...prev,
                { kind: "recommendation", result: data },
            ]);
        }
    };

    const canChat = Boolean(sessionId) && !isProcessing;

    const hasThreadContent =
        conversation.length > 0 || isProcessing || Boolean(agentError);

    return (
        <div className="space-y-3">
            <div
                className={`${hasThreadContent ? `${chatSurfaceClasses} p-3` : ""} space-y-3`}
                style={hasThreadContent ? chatSurfaceStyle : undefined}
            >
                {conversation.map((entry, index) => {
                    if (entry.kind === "user") {
                        return <UserChatBubble key={index} text={entry.text} />;
                    }
                    if (entry.kind === "reply") {
                        return (
                            <AgentChatBubble key={index} text={entry.text} />
                        );
                    }
                    return (
                        <div
                            key={index}
                            className={`${supportInsetPanelAuto} overflow-hidden`}
                        >
                            <div className={supportSubpanelHeader}>
                                <h4 className={supportContextCardTitle}>
                                    <Sparkles
                                        className="inline h-3.5 w-3.5 mr-1 text-ktx-purple -translate-y-px"
                                        aria-hidden
                                    />
                                    Agent recommendation
                                </h4>
                            </div>
                            <div className="px-3 py-3">
                                <RecommendationCard
                                    result={entry.result}
                                    ticketId={ticketId}
                                    sessionId={sessionId}
                                    buildShareUi={() => ({
                                        ticket_id: ticketId,
                                        conversation,
                                        cs_context: csContext || null,
                                    })}
                                />
                            </div>
                        </div>
                    );
                })}

                {isProcessing && <LiveActivity items={activity} />}

                {agentError && !isProcessing && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                        {agentError}
                    </p>
                )}

                <div ref={conversationEndRef} />
            </div>

            {!isProcessing && (
                <div className="pt-3 border-t border-gray-200">
                    <ChatComposer
                        value={chatInput}
                        onChange={setChatInput}
                        onSubmit={handleSendMessage}
                        disabled={!canChat}
                        placeholder={
                            canChat
                                ? "Reply to the agent — add details like patient name or appointment date…"
                                : conversation.length > 0
                                  ? "Chat unavailable — restart the analysis."
                                  : "Waiting for the analysis to complete…"
                        }
                    />
                </div>
            )}

            {!isProcessing && (
                <div className="flex justify-center pt-1">
                    <button
                        type="button"
                        onClick={onRestart}
                        className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-ktx-purple"
                    >
                        <RefreshCw className="h-3 w-3" aria-hidden />
                        Restart analysis
                    </button>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────

const TicketAnalysisPanel = ({
    ticketId,
    doctor,
    onPatientResolved,
    onSelectionChange,
    restoredSession = null,
}) => {
    // null = summary/form phase; object = analysis launched with this csContext
    const [analysisContext, setAnalysisContext] = useState(
        restoredSession ? restoredSession.csContext || {} : null,
    );
    const [analysisKey, setAnalysisKey] = useState(0);
    const [restored, setRestored] = useState(restoredSession);

    // Bubbled up from TicketConversation so the Share button can live in
    // the section header while the session state lives in the child.
    const [liveSessionId, setLiveSessionId] = useState(null);
    const liveConversationRef = useRef(null);

    const handleSessionReady = useCallback((sid, convRef) => {
        setLiveSessionId(sid);
        liveConversationRef.current = convRef;
    }, []);

    const handleStartAnalysis = (csContext) => {
        setRestored(null);
        setLiveSessionId(null);
        setAnalysisContext(csContext || {});
        setAnalysisKey((k) => k + 1);
    };

    const handleRestart = () => {
        if (
            liveSessionId &&
            !window.confirm(
                "Restart analysis? The current conversation will be lost.",
            )
        ) {
            return;
        }
        setRestored(null);
        setLiveSessionId(null);
        setAnalysisContext(null);
    };

    return (
        <SupportSection
            title="AI Assistant"
            summary={
                analysisContext
                    ? "Agent analysis and conversation"
                    : "Review the ticket summary and provide context"
            }
            defaultOpen
            headerRight={
                liveSessionId ? (
                    <ShareSessionButton
                        sessionId={liveSessionId}
                        buildUi={() => ({
                            ticket_id: ticketId,
                            conversation:
                                liveConversationRef.current?.current || [],
                            cs_context: analysisContext || null,
                        })}
                    />
                ) : null
            }
        >
            {/* Keep the form mounted (hidden) during analysis so selections,
          notes, and the fetched summary survive a "Start over". */}
            <div className={analysisContext ? "hidden" : undefined}>
                <TicketSummaryForm
                    ticketId={ticketId}
                    doctor={doctor}
                    onStartAnalysis={handleStartAnalysis}
                    onSelectionChange={onSelectionChange}
                />
            </div>
            {analysisContext !== null && (
                <TicketConversation
                    key={restored ? "restored" : analysisKey}
                    ticketId={ticketId}
                    csContext={analysisContext}
                    onRestart={handleRestart}
                    onPatientResolved={onPatientResolved}
                    onSessionReady={handleSessionReady}
                    restored={restored}
                />
            )}
        </SupportSection>
    );
};

export default TicketAnalysisPanel;
// Shared with the standalone agent chat on the Support homepage.
export {
    RecommendationCard,
    LiveActivity,
    UserChatBubble,
    AgentChatBubble,
    ChatComposer,
    ShareSessionButton,
    STEP_TOOL_LABELS,
    describeStepInput,
};
